import type { ImportResult } from '@flame/shared';
import { useState } from 'preact/hooks';
import { Api } from '../../api';
import { Field } from '../../components/ui';
import { attempt, loadPage } from '../../state';

type Format = 'yaml' | 'json' | 'netscape';

function detectFormat(name: string, content: string): Format {
  if (/\.html?$/i.test(name) || /NETSCAPE-Bookmark-file/i.test(content)) return 'netscape';
  if (/\.json$/i.test(name) || content.trimStart().startsWith('{')) return 'json';
  return 'yaml';
}

export function DataSettings() {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function runImport(e: Event) {
    e.preventDefault();
    if (!file) return;
    if (
      mode === 'replace' &&
      !confirm('Replace deletes all your current sections and links first. Continue?')
    ) {
      return;
    }
    setBusy(true);
    const content = await file.text();
    const res = await attempt(() =>
      Api.importData({ format: detectFormat(file.name, content), content, mode })
    );
    setBusy(false);
    if (res) {
      setResult(res);
      await loadPage();
    }
  }

  return (
    <>
      <h2>Export</h2>
      <p class="muted">
        Download everything on your page, including settings, as a YAML file you can edit by hand
        and import again. It also works as a backup.
      </p>
      <div class="form-actions">
        <a class="button button-primary" href="/api/data/export" download>
          Download YAML
        </a>
        <a class="button" href="/api/data/export?format=json" download>
          Download JSON
        </a>
      </div>

      <h2>Import</h2>
      <p class="muted">
        Import a file exported from here, or a bookmarks file exported from Chrome, Firefox,
        Safari or Edge (folders become sections).
      </p>
      <form class="form" onSubmit={runImport}>
        <Field label="File">
          <input
            type="file"
            required
            accept=".yaml,.yml,.json,.html,.htm"
            onChange={(e) => {
              setFile(e.currentTarget.files?.[0] ?? null);
              setResult(null);
            }}
          />
        </Field>
        <fieldset class="segmented">
          <legend class="field-label">Mode</legend>
          <label>
            <input type="radio" name="mode" checked={mode === 'merge'} onChange={() => setMode('merge')} />
            Merge: add new sections and links, skip ones you already have
          </label>
          <label>
            <input
              type="radio"
              name="mode"
              checked={mode === 'replace'}
              onChange={() => setMode('replace')}
            />
            Replace: delete your current sections, links and settings first
          </label>
        </fieldset>
        <div class="form-actions">
          <button type="submit" class="button button-primary" disabled={!file || busy}>
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </form>
      {result && (
        <p class="notice">
          Imported {result.linksCreated} link{result.linksCreated === 1 ? '' : 's'} and created{' '}
          {result.sectionsCreated} section{result.sectionsCreated === 1 ? '' : 's'}.
          {result.linksSkipped > 0 && ` Skipped ${result.linksSkipped} you already had or that weren't valid.`}
        </p>
      )}
    </>
  );
}
